import { enableProdMode, LOCALE_ID, importProvidersFrom, APP_INITIALIZER } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { OverlayContainer } from '@angular/cdk/overlay';
import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';
import { AppComponent } from './app/app.component';
import { ROUTES } from './app/app.routes';
import { DEPLOY_URL } from './app/core/injectors/deploy-url';
import { environment } from './environments/environment';
import { MEDIA_ASSETS_URL } from './app/app.config';
import { aiJwtInterceptor } from './app/core/interceptors/ai-jwt.interceptor';
import { TranslateModule } from '@ngx-translate/core';
import { TRANSLATE_MODULE_CONFIG, TRANSLATE_HTTP_LOADER_CONFIG_PROVIDER } from './app/translation/translation-config';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore } from '@ngrx/router-store';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import introspectionResult from './app/shared/graphql/introspection-result';
import { Apollo } from 'apollo-angular';
import { ConfigService } from './app/shared/services/config.service';
import { GlobalTimezoneService } from './app/shared/services/global-timezone.service';
import { HASHAUTHTOKEN } from './app/shared/services/hashaccess';
import { LANG } from './app/core/injectors';
import { APP_BASE_HREF, DatePipe, registerLocaleData } from '@angular/common';
import { provideQuillConfig } from 'ngx-quill';

// Import locale data
import localeEn from '@angular/common/locales/en';
import localePl from '@angular/common/locales/pl';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import {
  ACCESS_CONFIG,
  BASIC_TOKEN,
  CmBaseCompoundFilterIntl,
  CmObjectFieldList,
  CmObjectFieldListData,
  CmObjectFieldListIntl,
  CmObjectListData,
  CmPaginateTableIntl,
  CmUiCompoundFilterIntl,
  CmUiTableIntl,
  TopBarIntl,
  AccessService,
  AuthorizationInterceptor,
  TokenService,
  CONFIG_SERVICE,
  CmUiOverlayContainer
} from '@cm/ui-modules';
import { AccessConfig } from './app/acccess.config';
import { MENU_PROVIDER } from './app/shared/config/menu.config';
import { provideSharedServices } from './app/shared/shared-providers';
import * as CryptoJS from 'crypto-js';

if (environment.production) {
  enableProdMode();
  
  // Disable all console logs in production
  window.console.log = () => {};
  window.console.debug = () => {};
  window.console.info = () => {};
  window.console.warn = () => {};
  // Keep console.error for critical errors (optional - comment out to disable errors too)
  // window.console.error = () => {};
}

// Register locale data for all supported languages
registerLocaleData(localeEn, 'en');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeIt, 'it');
registerLocaleData(localePt, 'pt');


const deployUrl = (() => {
  const scripts = document.getElementsByTagName('script');
  const index = scripts.length - 1;
  const mainScript = scripts[index];
  return mainScript.src.replace(/main.*?\.js.*$/, '');
})();

export function initializeAppFactory(configService: ConfigService, tokenService: TokenService) {
  return () => initializeApp(configService, tokenService);
}
export async function initializeApp(configService: ConfigService, tokenService: TokenService): Promise<void> {
  // Step 1: Load config first — credentials (Basic Auth) become available after this
  try {
    const config = await firstValueFrom(configService.loadAppConfig());
    console.log('[App Init] Configuration loaded successfully');
    configService.setAppConfig(config);
  } catch (error) {
    console.error('[App Init] Error loading configuration:', error);
    // Continue anyway to allow app to start with default values
  }

  // Step 2: Restore the session from the backend-set cookies, if any (see
  // TokenService.bootstrapFromCookies — this is a no-op once cookies go httpOnly).
  try {
    await firstValueFrom(tokenService.bootstrapFromCookies());
  } catch (e) {
    console.warn('[App Init] Session bootstrap failed — user will need to log in', e);
  }

  // Feature flags that gate menu visibility (feature.approval) load themselves:
  // ApprovalService reacts to session activation, covering both the restored
  // session above and a fresh login later — no explicit step needed here.
}

export function langFactory(cookieService: CookieService): string {
  return cookieService.get('locale') || 'en';
}

// Create a functional interceptor wrapper for the class-based AuthorizationInterceptor
const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authorizationInterceptor = inject(AuthorizationInterceptor);
  return authorizationInterceptor.intercept(req, {
    handle: next
  });
};

export function createApollo(httpLink: HttpLink, tokenService: TokenService) {
  const apiConfiguration = {
    queryUrl: '/api/graphql'
  };

  const link = httpLink.create({ uri: apiConfiguration.queryUrl });
  const authLink = setContext((_, { headers }) => {
    const token = tokenService.getAccessToken();

    return {
      headers: {
        ...headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    } as any;
  });

  return {
    link: authLink.concat(link),
    cache: new InMemoryCache({possibleTypes: introspectionResult.possibleTypes}),
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(ROUTES),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor, aiJwtInterceptor])),
    provideNativeDateAdapter(),
    // One field look app-wide (ux-redesign phase 4): outline, never Material's grey fill.
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' } satisfies MatFormFieldDefaultOptions,
    },
    provideQuillConfig({
      modules: {
        toolbar: true
      }
    }),
    provideStore({}, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: true,
        strictActionSerializability: true,
        strictActionWithinNgZone: false, // DevTools uses asyncScheduler internally causing false positives
        strictActionTypeUniqueness: true,
      },
    }),
    provideEffects([]),
    provideRouterStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
      autoPause: true,
    }),
    TRANSLATE_HTTP_LOADER_CONFIG_PROVIDER,
    importProvidersFrom(
      TranslateModule.forRoot(TRANSLATE_MODULE_CONFIG)
    ),
    Apollo,
    HttpLink,
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, TokenService],
    },
    CookieService,
    ConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [ConfigService, TokenService],
      multi: true
    },
    {
      provide: HASHAUTHTOKEN,
      useValue: environment.hashauthtoken
    },
    {
      provide: LANG,
      useFactory: langFactory,
      deps: [CookieService]
    },
    {
      provide: DEPLOY_URL,
      useValue: deployUrl,
    },
    {
      provide: LOCALE_ID,
      useFactory: () => inject(CookieService).get('locale') || 'en',
    },
    DatePipe,
    GlobalTimezoneService,
    {
      provide: ACCESS_CONFIG,
      useValue: AccessConfig
    },
    {
      provide: BASIC_TOKEN,
      useFactory: (configService: ConfigService) => {
        // Create a class with a getter that dynamically retrieves the token
        return new class {
          toString(): string {
            const basicToken = configService.getBasicToken();
            if (basicToken) {
              console.log('[BASIC_TOKEN] Using config-provided credentials');
              return basicToken;
            }
            // Fallback to environment default only if config hasn't loaded
            console.warn('[BASIC_TOKEN] Config not loaded yet, using fallback credentials');
            return btoa(environment.basic_token_user.name + ':' + environment.basic_token_user.password);
          }
          valueOf(): string {
            return this.toString();
          }
        };
      },
      deps: [ConfigService]
    },
    {
      provide: CONFIG_SERVICE,
      useExisting: ConfigService
    },
    // cm-ui-dialog / CmUiDialogService pilot (docs/ui-modules.md § cm-ui-dialog): overlay-root
    // element becomes `cm-ui-overlay-container` app-wide instead of `cdk-overlay-container` —
    // see CmUiOverlayContainer for why this affects every overlay, not just the pilot dialogs.
    {
      provide: OverlayContainer,
      useClass: CmUiOverlayContainer
    },
    { provide: APP_BASE_HREF, useValue: environment.baseHref },
    {
      provide: MEDIA_ASSETS_URL,
      useValue: environment.assetBasePath
    },
    MENU_PROVIDER,
    CmBaseCompoundFilterIntl,
    CmObjectFieldList,
    CmObjectFieldListData,
    CmObjectFieldListIntl,
    CmObjectListData,
    CmPaginateTableIntl,
    CmUiCompoundFilterIntl,
    CmUiTableIntl,
    TopBarIntl,
    ...provideSharedServices()
  ]
}).catch(err => console.error(err));
